import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy } from '@ngneat/until-destroy';
import { map, Observable } from 'rxjs';
import { DealDto, AgeGroup } from 'src/app/model/deal.model';
import { HotelBookingService } from './hotel-booking.service';
import {
    MealPlanWithPrice,
    RoomWithCheapestMealPlan,
    transformInventoryDto,
} from './hotel-booking.model';
import { CurrencyLanguageService } from 'src/app/shared/currency-language.service';
import {
    CalendarComponent,
    CalendarDayInfo,
    CalendarSelectedDates,
} from 'src/app/controls/calendar/calendar.component';
import {
    BookingConfiguredData,
    emptyBookingConfiguredData,
} from '../booking-config.model';
import {
    createDateStringReverse,
    getDayDifference,
    getTodayNgb,
    getTomorrow,
} from 'src/app/controls/calendar/calendar.utils';
import { SearchOccupancyComponent } from 'src/app/components/search-occupancy/search-occupancy.component';
import { ActivatedRoute } from '@angular/router';
import * as _ from 'lodash';
import {
    OccupancyLimits,
    OccupancySelection,
} from '@app/components/search-occupancy/search-occupancy.model';
import {
    compileOccupancyString,
    compileOccupancyStringWithAges,
} from '@app/components/search-occupancy/search-occupancy.utils';

import { extractDatesAndOccupancyIdFromQueryParams } from '@app/components/call-to-action/call-to-action.utils';
import {
    computeCheapestMealPlans,
    getFirstAvailableStartAndEndDate,
    getMealPlansGroupedByRoomId,
    setOccupancyLimits,
} from './hotel-booking.utils';
import { HotelBookingStateService } from './hotel-booking.state.service';
import * as Sentry from '@sentry/angular-ivy';
import { GeneralErrorService } from '@app/components/general-error/general-error.service';

@UntilDestroy()
@Component({
    selector: 'md-hotel-booking',
    templateUrl: './hotel-booking.component.html',
    styleUrls: ['./hotel-booking.component.scss'],
})
export class HotelBookingComponent implements OnInit {
    @Input() set productDetailsInput(value: DealDto) {
        this.deal = value;
        this.hotelBookingService
            .fetchAgeGroups(this.deal)
            .subscribe((ageGroups) => {
                this.ageGroups = ageGroups;

                const dataFromQueryString =
                    extractDatesAndOccupancyIdFromQueryParams(
                        this.route.snapshot.queryParams
                    );
                if (dataFromQueryString) {
                    this.bootstrapHotelBookingFromSearch(dataFromQueryString);
                } else {
                    this.bootstrapHotelBookingFromPlp();
                }
                this.ref.markForCheck();
            });
    }

    additionalCalendarDayInfo: Map<string, CalendarDayInfo> = new Map();
    ageGroups: AgeGroup[];
    roomsWithCheapestMealPlans: RoomWithCheapestMealPlan[];
    mealPlansByRoomId: Record<number, MealPlanWithPrice[]>;
    selectedRoomId: number;
    selectedMealPlanId: number;
    currencySymbol: 'Rs.' | 'EUR';
    locale: 'en-MU' | 'fr-RE';

    fromDate: NgbDate;
    toDate: NgbDate;
    totalNights = 0;
    deal: DealDto;
    occupancyString = '2 Adults';
    occupancyId: number;
    selectedOccupancy: OccupancySelection;
    occupancyLimits: OccupancyLimits;

    isInvenotoryLoading = true;

    @Output() bookingConfigured = new EventEmitter<BookingConfiguredData>();
    @ViewChild('calendarHotel') calendarHotel: CalendarComponent;
    @ViewChild('occupancy') occupancy: SearchOccupancyComponent;

    constructor(
        public stateService: HotelBookingStateService,
        private hotelBookingService: HotelBookingService,
        private currencyLanguageService: CurrencyLanguageService,
        private route: ActivatedRoute,
        private ref: ChangeDetectorRef,
        private generalErrorService: GeneralErrorService
    ) {}

    get selectedRoomMealPlans(): MealPlanWithPrice[] {
        if (!this.selectedRoomId) {
            return [];
        }

        return this.mealPlansByRoomId[this.selectedRoomId];
    }

    ngOnInit(): void {
        this.currencyLanguageService.getLocaleData().subscribe((locale) => {
            this.currencySymbol = locale.currency;
            this.locale = locale.localeForCurrency;
        });
    }

    onMobileOccupancySearchClick(): void {
        // this.search();
        // this.mobileCTAOffcanvas.close();
    }

    onRoomChanged(room: RoomWithCheapestMealPlan): void {
        this.selectedRoomId = room.roomId;
        this.selectedMealPlanId =
            this.mealPlansByRoomId[this.selectedRoomId]?.[0].mealPlanId;
        this.triggerBookingConfigured();
    }

    onMealPlanChanged(mealPlan: MealPlanWithPrice): void {
        this.selectedMealPlanId = mealPlan.mealPlanId;
        this.triggerBookingConfigured();
    }

    onDateRangeSelected(selectedDates: CalendarSelectedDates): void {
        const { fromDate, toDate, totalNights } = selectedDates;
        this.stateService.addDatesAndOccupancyToQueryString(
            fromDate,
            toDate,
            this.selectedOccupancy
        );
        this.fromDate = fromDate;
        this.toDate = toDate;
        this.totalNights = totalNights;

        const fromDateAsString = createDateStringReverse(this.fromDate);
        this.getRoomOptionsAndMealPlans(
            this.occupancyId,
            fromDateAsString
        ).subscribe(() => {
            const mealPlanId = this.selectedRoomMealPlans?.[0].mealPlanId;
            this.getInventoryAndSetOnCalendar(
                this.occupancyId,
                mealPlanId
            ).subscribe(() => {
                this.occupancyLimits = setOccupancyLimits(
                    this.roomsWithCheapestMealPlans,
                    this.ageGroups
                );
                this.selectedRoomId =
                    this.stateService.getCheapestRoomIdForSelectedTimeframe(
                        fromDate,
                        toDate
                    );
                this.selectedMealPlanId =
                    this.mealPlansByRoomId[this.selectedRoomId]?.[0].mealPlanId;
                this.disableRoomOptionsBasedOnInventoryAndOccupancy(
                    fromDate,
                    toDate,
                    this.selectedOccupancy
                );

                this.triggerBookingConfigured();
            });
        });
    }

    onOccupancyChanged(occupancySelection: OccupancySelection): void {
        this.stateService.addDatesAndOccupancyToQueryString(
            this.fromDate,
            this.toDate,
            occupancySelection
        );
        this.selectedOccupancy = occupancySelection;
        this.occupancyString = compileOccupancyString(this.selectedOccupancy);
        // here need to get age groups of the deal to know what occupancyId to set
        this.hotelBookingService
            .extractOccupancyIdFromOccupancySelectionAndDealAgeGroups(
                occupancySelection,
                this.ageGroups
            )
            .subscribe((occupancyId) => {
                this.occupancyId = occupancyId;
                const fromDateAsString = createDateStringReverse(this.fromDate);
                this.getRoomOptionsAndMealPlans(
                    occupancyId,
                    fromDateAsString
                ).subscribe(() => {
                    const mealPlanId =
                        this.selectedRoomMealPlans?.[0].mealPlanId;
                    this.getInventoryAndSetOnCalendar(
                        occupancyId,
                        mealPlanId
                    ).subscribe(() => {
                        this.occupancyLimits = setOccupancyLimits(
                            this.roomsWithCheapestMealPlans,
                            this.ageGroups
                        );
                        this.disableRoomOptionsBasedOnInventoryAndOccupancy(
                            this.fromDate,
                            this.toDate,
                            occupancySelection
                        );
                        // if not all room options disabled, trigger booking configured
                        // if there is no inventory data it means that we're in a state
                        // where the occupancy is invalid but was not caught
                        if (
                            this.roomsWithCheapestMealPlans.some(
                                (room) => !room.isCurrentlyDisabled
                            )
                        ) {
                            this.triggerBookingConfigured();
                        } else if (this.additionalCalendarDayInfo.size === 0) {
                            this.bookingConfigured.emit(
                                emptyBookingConfiguredData
                            );
                        }
                        // else, the user will have to select a different date range
                        // so open the calendar and clear dates
                        else {
                            this.calendarHotel.openCalendar(true);
                            this.fromDate = null;
                            this.toDate = null;
                        }
                    });
                });
            });
    }

    onOccupancyClicked(): void {
        this.occupancy.openOccupancy();
    }

    onDatesClick() {
        this.calendarHotel.openCalendar(false, this.fromDate, this.toDate);
    }

    triggerBookingConfigured(): void {
        if (
            !this.fromDate ||
            !this.toDate ||
            !this.selectedRoomId ||
            !this.selectedMealPlanId ||
            !this.occupancyId
        ) {
            return;
        }

        this.hotelBookingService
            .fetchInventoryForMealPlanAndRoom(
                this.deal,
                this.selectedMealPlanId,
                this.selectedRoomId,
                this.occupancyId
            )
            .subscribe(({ inventory }) => {
                const { sellingPrice, crossedPrice, crossedPercent } =
                    this.stateService.computePriceBasedOnRoomAndMealPlanSelection(
                        inventory,
                        this.fromDate,
                        this.toDate,
                        this.selectedRoomId,
                        this.selectedRoomMealPlans[0]?.crossedPercent || 0
                    );

                const bookingConfiguredData: BookingConfiguredData = {
                    totalPrice: sellingPrice,
                    totalFullPrice: crossedPrice,
                    flightPrice: 0,
                    productBookingData: {
                        id: this.deal.dealId,
                        selectedAccomOptions: [
                            {
                                mealPlanId: this.selectedMealPlanId,
                                mealPlanType: this.selectedRoomMealPlans.find(
                                    (mealPlan) =>
                                        mealPlan.mealPlanId ===
                                        this.selectedMealPlanId
                                ).mealPlanType,
                                roomId: this.selectedRoomId,
                                roomName: this.roomsWithCheapestMealPlans.find(
                                    (room) =>
                                        room.roomId === this.selectedRoomId
                                ).roomName,
                                sellingPrice,
                                crossedPrice,
                                crossedPercent,
                                occupancyId: this.occupancyId,
                                occupancyString: this.occupancyString,
                                occupancyStringWithAges:
                                    compileOccupancyStringWithAges(
                                        this.occupancyString,
                                        this.selectedOccupancy
                                    ),
                            },
                        ],
                        checkinDate: this.fromDate,
                        checkoutDate: this.toDate,
                        totalNights: this.totalNights,
                    },
                };
                this.bookingConfigured.emit(bookingConfiguredData);
            });
    }

    private bootstrapHotelBookingCommon(
        fromDate: NgbDate,
        toDate: NgbDate,
        occupancy: OccupancySelection
    ) {
        this.fromDate = fromDate;
        this.toDate = toDate;
        this.totalNights = getDayDifference(fromDate, toDate);
        this.selectedOccupancy = occupancy;
        this.occupancyString = compileOccupancyString(occupancy);
    }
    private bootstrapHotelBookingFromPlp() {
        this.occupancyId = 1332; // 2 Adults
        this.isInvenotoryLoading = true;

        const fromDateAsString = createDateStringReverse(getTodayNgb());
        this.getRoomOptionsAndMealPlans(
            this.occupancyId,
            fromDateAsString
        ).subscribe(() => {
            const mealPlanId = this.selectedRoomMealPlans?.[0].mealPlanId;
            this.getInventoryAndSetOnCalendar(
                this.occupancyId,
                mealPlanId
            ).subscribe(() => {
                const { startDate, endDate } = getFirstAvailableStartAndEndDate(
                    this.additionalCalendarDayInfo
                );
                this.bootstrapHotelBookingCommon(startDate, endDate, {
                    adults: 2,
                    children: [],
                });
                this.occupancyLimits = setOccupancyLimits(
                    this.roomsWithCheapestMealPlans,
                    this.ageGroups
                );
                this.disableRoomOptionsBasedOnInventoryAndOccupancy(
                    startDate,
                    endDate,
                    this.selectedOccupancy,
                    true
                );
                this.triggerBookingConfigured();
                this.isInvenotoryLoading = false;
                this.ref.markForCheck();
            });
        });

        Sentry.captureEvent({
            message: 'Stay deal opened from plp',
            level: 'info',
            tags: {
                action: 'search_result_selected',
                result_name: this.deal.dealTitle,
            },
        });
    }
    private bootstrapHotelBookingFromSearch(dataFromQueryString: {
        fromDate: NgbDate;
        toDate: NgbDate;
        occupancy: OccupancySelection;
    }) {
        this.isInvenotoryLoading = true;
        const { fromDate, toDate, occupancy } = dataFromQueryString;
        this.bootstrapHotelBookingCommon(fromDate, toDate, occupancy);
        const fromDateAsString = createDateStringReverse(fromDate);
        this.hotelBookingService
            .extractOccupancyIdFromOccupancySelectionAndDealAgeGroups(
                occupancy,
                this.ageGroups
            )
            .subscribe((occupancyId) => {
                this.occupancyId = occupancyId;
                this.getRoomOptionsAndMealPlans(
                    occupancyId,
                    fromDateAsString
                ).subscribe(() => {
                    const mealPlanId =
                        this.selectedRoomMealPlans?.[0].mealPlanId;
                    this.getInventoryAndSetOnCalendar(
                        occupancyId,
                        mealPlanId
                    ).subscribe(() => {
                        this.occupancyLimits = setOccupancyLimits(
                            this.roomsWithCheapestMealPlans,
                            this.ageGroups
                        );
                        this.disableRoomOptionsBasedOnInventoryAndOccupancy(
                            fromDate,
                            toDate,
                            occupancy,
                            true
                        );
                        this.isInvenotoryLoading = false;
                        this.triggerBookingConfigured();
                    });
                });
            });
        Sentry.captureEvent({
            message: 'Stay deal opened from search',
            level: 'info',
            tags: {
                action: 'search_result_selected',
                result_name: this.deal.dealTitle,
            },
        });
    }

    private getRoomOptionsAndMealPlans(
        occupancyId: number,
        startDate = getTomorrow()
    ): Observable<void> {
        return this.hotelBookingService
            .fetchAccomOptions(this.deal, startDate, occupancyId)
            .pipe(
                map((accomOptions) => {
                    this.roomsWithCheapestMealPlans = computeCheapestMealPlans(
                        accomOptions.options
                    );
                    this.mealPlansByRoomId = getMealPlansGroupedByRoomId(
                        accomOptions.options,
                        this.roomsWithCheapestMealPlans
                    );
                    this.selectedRoomId =
                        this.roomsWithCheapestMealPlans?.[0].roomId;

                    this.selectedMealPlanId =
                        this.mealPlansByRoomId[
                            this.selectedRoomId
                        ]?.[0].mealPlanId;
                    this.ref.markForCheck();
                })
            );
    }

    private getInventoryAndSetOnCalendar(
        occupancyId: number,
        mealPlanId: number
    ): Observable<void> {
        return this.hotelBookingService
            .fetchAccomInventory(this.deal, occupancyId, mealPlanId)
            .pipe(
                map(({ inventory }) => {
                    this.additionalCalendarDayInfo =
                        this.stateService.computeAdditionalCalendarDayInfo(
                            transformInventoryDto(inventory),
                            this.roomsWithCheapestMealPlans
                        );
                    this.ref.markForCheck();
                })
            );
    }

    private disableRoomOptionsBasedOnInventoryAndOccupancy(
        fromDate: NgbDate,
        toDate: NgbDate,
        occupancy: OccupancySelection,
        isFromPageBootstrap = false
    ) {
        const roomIdsWithAvailability =
            this.stateService.getRoomsAndInventoryAvailableForAllDates(
                fromDate,
                toDate
            ).roomIds;
        this.roomsWithCheapestMealPlans = this.roomsWithCheapestMealPlans.map(
            (room) => {
                const { isDisabled, reason } =
                    this.stateService.isRoomOptionDisabledAndReason(
                        room,
                        roomIdsWithAvailability,
                        occupancy,
                        this.ageGroups,
                        this.occupancyString
                    );
                return {
                    ...room,
                    isCurrentlyDisabled: isDisabled,
                    reasonForDisabled: reason,
                };
            }
        );

        const firstRoomIdThatIsNotDisabled =
            this.roomsWithCheapestMealPlans.find(
                (room) => !room.isCurrentlyDisabled
            )?.roomId;
        if (firstRoomIdThatIsNotDisabled) {
            this.selectedRoomId = firstRoomIdThatIsNotDisabled;
        } else {
            this.selectedRoomId = undefined;
            this.selectedMealPlanId = undefined;
            this.generalErrorService.showGeneralError(
                'It seems there are no rooms available for the selected dates or guests. Please try a different deal or contact us for assistance.'
            );
            // this means that all rooms are disabled. This should not happen if search works well
            // so log to Sentry
            Sentry.captureEvent({
                message: 'No room options for configuration',
                level: 'error',
                tags: {
                    result_name: this.deal.dealTitle,
                    guests: this.occupancyString,
                    checkin: createDateStringReverse(fromDate),
                    checkout: createDateStringReverse(toDate),
                },
            });
        }
        this.ref.markForCheck();
    }
}
